/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 */

import { useNavigation } from '@/contexts/NavigationContext';
import { useState, useEffect } from 'react';

interface GlobalFooterProps {
  onOpenPortal?: () => void;
}

export function GlobalFooter({ onOpenPortal }: GlobalFooterProps = {}) {
  const currentYear = new Date().getFullYear();
  const { navigate } = useNavigation();
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

  // Check if user is authorized to see portal link
  useEffect(() => {
    const subscriberEmail = localStorage.getItem('subscriberEmail') || '';
    const isAuthorized = subscriberEmail.endsWith('@oratf.info') || subscriberEmail === 'carapaulson1@gmail.com';
    setIsAuthorizedUser(isAuthorized);
  }, []);

  const communityLinks = [
    { label: 'Discord', href: 'https://discord.gg/ora', external: true },
    { label: 'Events', href: '#events' },
  ];

  const resourceLinks = [
    { label: 'Documentation', href: '/docs' },
    { label: 'AI KPI Dashboard', href: '/analytics' },
    { label: 'Free Training', href: 'https://agent.myora.now/', external: true },
  ];

  const companyLinks = [
    { label: 'About', href: '/about' },
    { label: 'Mission', href: '/mission' },
    { label: 'Pricing Options', href: '/pricing' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '#privacy' },
    { label: 'Terms of Service', href: '#terms' },
    { label: 'User Agreement', href: '/agreement' },
    { label: 'Agent Models', href: '/agent-models' },
    { label: 'QR Codes', href: '/qrcodehub' },
    { label: 'Order History', href: '/order-history' },
    { label: 'Code of Conduct', href: '#code-of-conduct' },
  ];

  const handleLinkClick = (href: string, external?: boolean) => {
    if (external) {
      window.open(href, '_blank', 'noopener noreferrer');
    } else if (href.startsWith('#')) {
      // Handle hash links (scroll to section)
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Use custom navigation via event
      navigate(href);
    }
  };

  return (
    <footer className="border-t border-gray-800 bg-black">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 bg-black">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-6">
          {/* ORA Brand Section - Spans 2 columns on desktop */}
          <div className="md:col-span-2">
            <a
              href="/training"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/training');
              }}
              className="text-xl font-bold mb-3 inline-block !text-white hover:text-primary transition-colors cursor-pointer"
            >
              ORA
            </a>
            <p className="text-sm !text-white leading-relaxed mb-3">
              Collaboration and teamwork is vital for driving digital transformation in today's workforce. Our fast, free, mobile micro-learning platform is AI-enabled, making AI literacy accessible. Together, we can foster innovation, boost efficiency, and achieve measurable organizational! Options available in both English and Spanish. Start learning now!
            </p>
            <a
              href="/agreement"
              onClick={(e) => {
                e.preventDefault();
                handleLinkClick('/agreement');
              }}
              className="text-sm text-primary hover:underline font-medium inline-block"
            >
              Free Trial User Agreement
            </a>
          </div>

          {/* Community Links */}
          <div>
            <h3 className="text-sm font-semibold !text-white mb-3">Community</h3>
            <ul className="space-y-2">
              {communityLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(link.href, link.external);
                    }}
                    className="text-sm !text-white hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="text-sm font-semibold !text-white mb-3">Resources</h3>
            <ul className="space-y-2">
              {resourceLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(link.href, link.external);
                    }}
                    className="text-sm !text-white hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold !text-white mb-3">Company</h3>
            <ul className="space-y-2">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleLinkClick(link.href, link.external);
                    }}
                    className="text-sm !text-white hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Legal Section - Reorganized for better mobile/desktop layout */}
        <div className="pt-6 border-t border-gray-800">
          {/* Legal Links - Now on top for better organization */}
          <nav className="flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2 mb-4">
            {legalLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick(link.href);
                }}
                className="text-xs !text-white hover:text-primary transition-colors"
              >
                {link.label}
              </a>
            ))}
            
            {/* Admin Portal Link - Only visible to authorized users */}
            {isAuthorizedUser && onOpenPortal && (
              <button
                onClick={onOpenPortal}
                className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors font-semibold"
              >
                Portal
              </button>
            )}
          </nav>

          {/* Copyright - Below legal links */}
          <div className="flex justify-center lg:justify-start">
            <p className="text-xs !text-white">
              © {currentYear} ORA-AI LLC. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}